<script>
import {
  iAccordion,
  iAccordionItem,
  iAccordionItemSizes,
} from '@inscribe/signal';
import DocumentRightDataBox from '@/V2/documents/document-view/DocumentRightDataBox';
import signalConfig from '@/constants/signalConfig';
import {
  VERIFICATION_RESULT_SORT_ORDER,
  ANALYST_GROUPS,
  RISK_LEVEL,
} from '@/constants/analyst-constants';
import InfoIcon from '@/V2/components/InfoIcon';
import { SEVERITY_LEVEL_BY_FRAUD_SIGNAL } from '@/constants/signalNames';
import {
  extendPages,
  getSignalName,
  getSignalSubtitle,
  getSignalLink,
  getBaseRoute,
  signalHasRegions,
  signalHasDetailsView,
} from '@/utils/signalNames';
import { mapGetters } from 'vuex';
import SignalDetails from '@/V2/documents/document-view/signals/SignalDetails.vue';
import SignalMetadata from '@/V2/documents/document-view/signals/SignalMetadata.vue';
import { trustSignalsConfig } from '@/constants/trustSignalsConfig';

export default {
  components: {
    DocumentRightDataBox,
    iAccordion,
    iAccordionItem,
    InfoIcon,
    SignalDetails,
    SignalMetadata,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
    currentFullGroup: {
      type: String,
    },
    currentVerificationComponent: {
      type: String,
    },
    currentVerification: {
      type: Object,
    },
    showLoadingState: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iAccordionItemSizes,
      signalConfig,
      signalHasRegions,
      signalHasDetailsView,
      viewFullGroup: false,
      trustSignalsConfig,
    };
  },
  watch: {
    currentFullGroup(newVal) {
      if (newVal === 'signals') {
        this.openFullGroup();
      } else {
        this.viewFullGroup = false;
      }
    },
  },
  computed: {
    ...mapGetters(['showTransactionsSignals']),
    /**
     * the groups generated from the following methods are logical groups
     * Groups are defined in the analyst-config.js file
     * A group will have this data structure
     * {
     *   key: 'string',
     *   riskLevels: [
     *      {
     *        label: 'string',
     *        verifications: []
     *      }
     *   ]
     * }
     */
    fraudSignalsGroup() {
      const returnGroup = {
        key: ANALYST_GROUPS.FRAUD,
        label: 'Signals',
        riskLevels: [],
      };
      let fraudSignalsCount = 0;
      // fraud signals
      Object.keys(this.document.fraud_results || {}).forEach((signalKey) => {
        const fraudObject = this.document.fraud_results[signalKey];
        if (fraudObject.failed) {
          const severityLevel = SEVERITY_LEVEL_BY_FRAUD_SIGNAL[signalKey];
          let foundLevel = returnGroup.riskLevels.find(
            (level) => level.key === severityLevel,
          );
          if (!foundLevel) {
            foundLevel = {
              key: severityLevel,
              verifications: [],
            };
            returnGroup.riskLevels.push(foundLevel);
          }
          const flattenSignal = {
            ...fraudObject,
            signalKey,
            ...fraudObject.details,
            pages: fraudObject.details.pages,
          };
          extendPages(
            flattenSignal.details?.pages || [],
            signalKey,
            this.getDisabledInstances(),
            this.getDisabledGroups(),
            this.$route,
            this.document,
          );
          foundLevel.verifications.push({
            ...fraudObject,
            rationale: getSignalSubtitle(flattenSignal),
            result: severityLevel,
            title: getSignalName(signalKey),
            subtitle: getSignalSubtitle(flattenSignal),
            type: signalKey,
            signalKey,
            signalObject: flattenSignal,
            detailsType: 'fraud',
            hasRegions: signalHasRegions(fraudObject, signalKey),
            helpArticle: this.signalConfig[signalKey]?.helpArticle,
            link: getSignalLink(
              flattenSignal,
              getBaseRoute(this.$route, this.document),
            ),
            enabled: !this.getDisabledGroups().includes(signalKey),
          });
          fraudSignalsCount++;
        }
      });

      if (this.showTransactionsSignals) {
        fraudSignalsCount += this.processTransactionSignals(returnGroup);
      }

      // trust signals
      const trust_signals = [];
      for (const signalKey of Object.keys(this.document.trust_signals || {})) {
        const signalConfig = this.trustSignalsConfig[signalKey];
        if (signalConfig) {
          trust_signals.push({
            key: `${signalKey}`,
            title: signalConfig.title,
            subtitle: signalConfig.subtitle,
            order: signalConfig.order,
            detailsType: 'fraud',
            result: RISK_LEVEL.TRUST,
            helpArticle:
              'https://help.inscribe.ai/en/articles/8976050-trust-signals',
          });
        }
      }

      // sort
      if (trust_signals.length) {
        trust_signals.sort((t1, t2) => {
          return t1.order > t2.order ? 1 : -1;
        });

        // add to return group
        returnGroup.riskLevels.push({
          key: RISK_LEVEL.TRUST,
          verifications: trust_signals,
        });
      }

      // sort group by severity
      returnGroup.riskLevels.sort((r1, r2) => {
        return VERIFICATION_RESULT_SORT_ORDER[r1.key] >
          VERIFICATION_RESULT_SORT_ORDER[r2.key]
          ? 1
          : -1;
      });

      // subtitle;
      returnGroup.subtitle = `${fraudSignalsCount} fraud ${
        fraudSignalsCount === 1 ? 'signal' : 'signals'
      } detected`;

      this.$emit('update:subtitle', returnGroup.subtitle);

      return returnGroup;
    },
    fraudTotalCount() {
      let count = 0;
      this.fraudSignalsGroup.riskLevels.forEach((level) => {
        count += level.verifications.length;
      });
      return count;
    },
  },
  methods: {
    ...mapGetters({
      getDisabledInstances: 'getDisabledInstances',
      getDisabledGroups: 'getDisabledGroups',
    }),
    processTransactionSignals(returnGroup) {
      let count = 0;
      const signals = {
        ...this.document.transaction_signals,
      };

      Object.keys(signals).forEach((signalKey) => {
        const txSignalObject = signals[signalKey];
        const txSignalConfig = signalConfig[signalKey];
        if (txSignalConfig && txSignalObject.flagged) {
          const severityLevel = SEVERITY_LEVEL_BY_FRAUD_SIGNAL[signalKey];
          let foundLevel = returnGroup.riskLevels.find(
            (level) => level.key === severityLevel,
          );
          if (!foundLevel) {
            foundLevel = {
              key: severityLevel,
              verifications: [],
            };
            returnGroup.riskLevels.push(foundLevel);
          }
          foundLevel.verifications.push({
            ...txSignalObject,
            result: severityLevel,
            title: signalConfig[signalKey]?.title,
            subtitle: signalConfig[signalKey]?.description,
            type: signalKey,
            signalKey,
            signalObject: { ...txSignalObject },
            detailsType: 'transaction',
          });
          count++;
        }
      });
      return count;
    },
    getSignalDetailsGroup(signal) {
      const finalSignal = signal.signalObject;
      return {
        ...signal.signalObject,
        customerId: '',
        documentId: '',
        enabled: true,
        link: '',
        signals: [finalSignal],
        subTitle: signal.subtitle,
        title: signal.title,
      };
    },
    openFullGroup() {
      this.viewFullGroup = true;
      this.$nextTick(() => {
        this.$emit('open-overlay', this.$refs.documentReviewGroupFraud);
      });
    },
    openItemDetails(verification) {
      // trust signals have no details page
      if (verification.result === RISK_LEVEL.TRUST) {
        return;
      }

      const signal = verification.signalObject;
      if (signalHasRegions(signal, signal?.signalKey)) {
        this.$emit('set-verification', verification);
        this.$emit('set-verification-component', 'details');
        this.$nextTick(() => {
          this.$emit('open-overlay', this.$refs.signalDetails);
        });
      } else if (
        signalHasDetailsView(signal.signalKey || verification.signalKey)
      ) {
        this.$emit('set-verification', verification);
        this.$emit('set-verification-component', 'metadata');
        this.$nextTick(() => {
          this.$emit('open-overlay', this.$refs.signalMetadata);
        });
      } else {
        this.$router
          .push(getSignalLink(signal, getBaseRoute(this.$route, this.document)))
          .catch(() => {});
      }
    },
  },
};
</script>

<template>
  <template>
    <!-- Fraud Signals -->
    <i-accordion-item
      :mount-open="true"
      title="Signals"
      :no-background="true"
      :size="iAccordionItemSizes.LARGE"
      class="half-margin"
      data-testid="signals-group"
    >
      <!-- title extra content -->
      <template #extra-title-content>
        <info-icon
          class="i-mr-4"
          title="Signals"
          description="Highlights signs of fraudulent activity, such as document modifications, unusual formatting, and suspicious metadata."
          link-text="Learn more"
          link-external-url="https://help.inscribe.ai/en/"
        />
      </template>

      <!-- group content -->
      <document-right-data-box
        :is-loading="showLoadingState"
        :group="fraudSignalsGroup"
        :compact-version="true"
        empty-state-title="No fraud signals detected"
        empty-state-subtitle="Document fraud signals will appear here"
        empty-state-label="Manage Signals"
        empty-state-icon="medium_file_alert_1"
        :show-loading-state="false"
        @show-item-details="openItemDetails"
        @view-all="openFullGroup(fraudSignalsGroup)"
        @empty-state-btn-click="$emit('open-decision-engine')"
      ></document-right-data-box>
    </i-accordion-item>

    <!-- fraud full group -->
    <document-right-data-box
      v-if="viewFullGroup"
      ref="documentReviewGroupFraud"
      :is-loading="showLoadingState"
      :group="fraudSignalsGroup"
      @show-item-details="openItemDetails"
    ></document-right-data-box>

    <!-- signal details -->
    <signal-details
      v-if="
        currentVerification?.detailsType === 'fraud' &&
        currentVerificationComponent === 'details'
      "
      ref="signalDetails"
      :signal-group="getSignalDetailsGroup(currentVerification)"
      :signal="currentVerification"
    ></signal-details>

    <signal-metadata
      v-if="
        ['fraud', 'transaction'].includes(currentVerification?.detailsType) &&
        currentVerificationComponent === 'metadata'
      "
      ref="signalMetadata"
      :document="document"
      :signal-group="getSignalDetailsGroup(currentVerification)"
      :signal="currentVerification"
    ></signal-metadata>
  </template>
</template>
